<script setup>
// "DESFAZER ENVIO" NO WHATSAPP OFICIAL (20/10/2026).
// A API oficial não apaga mensagem que já saiu (docs/WHATSAPP-OFICIAL.md). A mensagem digitada pela equipe espera
// alguns segundos no servidor (Advup::Wame::DesfazerEnvio); enquanto espera, este cartão aparece embaixo da bolha:
// o anel esvazia com o tempo, o texto diz que o cliente ainda não recebeu, e o botão desfaz. Desfeita, ela não sai e
// fica riscada como "envio desfeito".
//
// UM BALÃO SÓ (22/10/2026, Advup::Wame::BalaoUnico): mensagens seguidas da mesma pessoa, enquanto esperam, formam um
// balão e saem numa mensagem só (a Meta cobra cada balão). Enquanto esperam, o cartão diz que vão juntas; depois que
// saíram, fica a marca discreta "Saiu num balão só" — o histórico mostra o que cada um digitou e o que o cliente leu.
import { computed, ref, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'vuex';
import MessageApi from 'dashboard/api/inbox/message';
import { useEnvioSegurado } from './useEnvioSegurado';

const props = defineProps({
  conversationId: { type: Number, required: true },
  messageId: { type: Number, required: true },
  contentAttributes: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const store = useStore();
const desfazendo = ref(false);
const { emEspera, segundos, fracao } = useEnvioSegurado(
  toRef(props, 'contentAttributes')
);

// O store da conversa pode trazer as chaves em camelCase ou como vieram do servidor.
const atributo = chave => {
  const camel = chave.replace(/_([a-z])/g, (_, letra) => letra.toUpperCase());
  const atributos = props.contentAttributes || {};
  return atributos[camel] ?? atributos[chave];
};
const noBalao = computed(() => !!atributo('balao'));
// Pela contagem, como o próprio cartão: a saída é gravada sem avisar a tela (a ponte não pode ver essa atualização).
const saiuNoBalao = computed(
  () => noBalao.value && !emEspera.value && !atributo('envio_desfeito')
);

// O anel: um círculo de raio 15 (circunferência ~94,25) que esvazia.
const CIRCUNFERENCIA = 2 * Math.PI * 15;

async function desfazer() {
  if (desfazendo.value) return;
  desfazendo.value = true;
  try {
    const { data } = await MessageApi.desfazerEnvio(
      props.conversationId,
      props.messageId
    );
    // A tela risca na hora, com a mensagem que o servidor devolveu.
    if (data?.id) store.dispatch('updateMessage', data);
    useAlert(t('CONVERSATION.DESFAZER_ENVIO.DESFEITO'));
  } catch (erro) {
    useAlert(
      erro?.response?.data?.error || t('CONVERSATION.DESFAZER_ENVIO.JA_SAIU')
    );
  } finally {
    desfazendo.value = false;
  }
}
</script>

<template>
  <Transition name="dse">
    <div v-if="emEspera" class="dse" role="status" aria-live="polite">
      <span class="dse__anel" aria-hidden="true">
        <svg viewBox="0 0 36 36">
          <circle class="dse__trilho" cx="18" cy="18" r="15" />
          <circle
            class="dse__resto"
            cx="18"
            cy="18"
            r="15"
            :stroke-dasharray="CIRCUNFERENCIA"
            :stroke-dashoffset="CIRCUNFERENCIA * (1 - fracao)"
          />
        </svg>
        <span class="dse__num">{{ segundos }}</span>
      </span>
      <span class="dse__texto">
        <strong class="dse__tit">
          {{ t('CONVERSATION.DESFAZER_ENVIO.SAINDO', { segundos }) }}
        </strong>
        <span class="dse__dica">{{
          noBalao
            ? t('CONVERSATION.DESFAZER_ENVIO.BALAO_DICA')
            : t('CONVERSATION.DESFAZER_ENVIO.DICA')
        }}</span>
      </span>
      <button
        type="button"
        class="advup-btn-forte advup-btn--compacto reset-base dse__botao"
        :disabled="desfazendo"
        @click="desfazer"
      >
        <i
          :class="
            desfazendo ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-undo-2'
          "
          aria-hidden="true"
        />
        {{ t('CONVERSATION.DESFAZER_ENVIO.BOTAO') }}
      </button>
    </div>
    <p
      v-else-if="saiuNoBalao"
      v-tooltip.top="t('CONVERSATION.DESFAZER_ENVIO.BALAO_EXPLICA')"
      class="dse-balao"
    >
      <i class="i-lucide-layers" aria-hidden="true" />
      {{ t('CONVERSATION.DESFAZER_ENVIO.BALAO_SAIU') }}
    </p>
  </Transition>
</template>

<style scoped>
.dse {
  display: flex;
  align-items: center;
  gap: var(--esp-3);
  margin-top: var(--esp-2);
  padding: var(--esp-2) var(--esp-2) var(--esp-2) var(--esp-3);
  border: 1px solid rgb(var(--slate-5));
  border-radius: var(--r-4);
  background: rgb(var(--solid-1));
  box-shadow: 0 6px 18px -10px rgb(15 23 42 / 0.35);
}

.dse__anel {
  position: relative;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
}

.dse__anel svg {
  position: absolute;
  inset: 0;
  transform: rotate(-90deg);
}

.dse__trilho,
.dse__resto {
  fill: none;
  stroke-width: 3;
}

.dse__trilho {
  stroke: rgb(var(--slate-4));
}

.dse__resto {
  stroke: rgb(var(--blue-9));
  stroke-linecap: round;
  transition: stroke-dashoffset 0.1s linear;
}

.dse__num {
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  font-variant-numeric: tabular-nums;
  color: rgb(var(--slate-12));
}

.dse__texto {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.3;
}

.dse__tit {
  font-size: var(--txt-corpo);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-12));
}

.dse__dica {
  font-size: var(--txt-nota);
  color: rgb(var(--slate-11));
}

.dse__botao {
  margin-left: var(--esp-2);
  white-space: nowrap;
}

.dse-balao {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 2px 0 0;
  font-size: var(--txt-selo, 11.5px);
  color: rgb(var(--slate-10));
  cursor: default;
}

.dse-balao > i {
  width: 12px;
  height: 12px;
}

.dse-enter-active,
.dse-leave-active {
  transition:
    opacity var(--mov-2, 0.18s) ease,
    transform var(--mov-2, 0.18s) ease;
}

.dse-enter-from,
.dse-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
